package pl.nickers.match3.ecs

import kotlin.random.Random

internal class FamiliarSystem(
    private val random: Random = Random.Default,
    private val pieceSet: PieceSet = PieceSetLoader.default(),
) : BaseSystem() {
    private lateinit var familiarMapper: ComponentMapper<FamiliarComponent>
    private lateinit var typeMapper: ComponentMapper<JellyTypeComponent>
    private lateinit var bodyImageMapper: ComponentMapper<BodyImageComponent>
    private lateinit var goalsMapper: ComponentMapper<GoalsComponent>
    private lateinit var pieceIdMapper: ComponentMapper<PieceIdComponent>
    private lateinit var posMapper: ComponentMapper<GridPositionComponent>
    private lateinit var faceMapper: ComponentMapper<JellyFaceComponent>

    override fun initialize() {
        familiarMapper = world.mapper()
        typeMapper = world.mapper()
        bodyImageMapper = world.mapper()
        goalsMapper = world.mapper()
        pieceIdMapper = world.mapper()
        posMapper = world.mapper()
        faceMapper = world.mapper()
    }

    override fun processSystem() {
        val board = world.boardState() ?: return
        if (board.phase != GamePhase.RESOLVE) return
        if (board.won) return

        val familiarEntity = world.getEntitiesForAspect(Aspect.all(FamiliarComponent::class))
            .firstOrNull() ?: return
        val familiar = familiarMapper[familiarEntity] ?: return

        while (familiar.progress >= familiar.threshold) {
            val acted = when (familiar.type) {
                FamiliarType.CONJURER -> activateConjurer(familiar)
                FamiliarType.BLACK_CAT -> activateBlackCat()
                else -> false // remaining roadmap familiar types are implemented in E11
            }
            if (!acted) return
            // The Conjurer can transmute pieces into fresh matches, so re-run gravity/resolution.
            if (familiar.type == FamiliarType.CONJURER) board.didWork = true
            familiar.progress -= familiar.threshold
        }
    }

    // ---------------------------------------------------------------------
    // Conjurer: transmute up to 3 random standard jellies into the trigger
    // ingredient. (Replacing existing jellies rather than filling empty cells,
    // since a board in normal play is almost always full.)
    // ---------------------------------------------------------------------

    private fun activateConjurer(familiar: FamiliarComponent): Boolean {
        val targetPieceId = familiar.triggerPieceId
            ?: firstIncompleteGoalPieceId()
            ?: return false
        val candidates = replaceableJellies(targetPieceId).shuffled(random)
        if (candidates.isEmpty()) return false
        candidates.take(CONJURER_REPLACE_COUNT).forEach { transmute(it, targetPieceId) }
        return true
    }

    // ---------------------------------------------------------------------
    // Black Cat: transmute one random standard jelly into the most-needed goal type.
    // ---------------------------------------------------------------------

    private fun activateBlackCat(): Boolean {
        val goalsComp = world.boardEntityId()?.let { goalsMapper[it] } ?: return false
        val incompleteGoals = goalsComp.goals.filter { !it.completed }
        if (incompleteGoals.isEmpty()) return false

        val targetPieceId = selectTargetPieceId(incompleteGoals) ?: return false
        val replacement = replaceableJellies(targetPieceId).randomOrNull(random) ?: return false
        transmute(replacement, targetPieceId)
        return true
    }

    /** Rewrites [entity] in place to be the [targetPieceId] ingredient. */
    private fun transmute(entity: Int, targetPieceId: String) {
        val targetType = jellyTypeForPieceId(targetPieceId)
        val face = faceMapper[entity]?.image ?: "face_1.png"
        val pos = posMapper[entity]
        typeMapper.set(entity, JellyTypeComponent(type = targetType))
        val body = pieceSet.pieces[targetPieceId]?.sprites?.body ?: "jelly_$targetType.png"
        bodyImageMapper.set(entity, BodyImageComponent(image = body))
        pieceIdMapper.set(entity, PieceIdComponent(targetPieceId))
        // Visual hint: a sprite flies from the familiar display to the transmuted cell.
        if (pos != null) world.enqueueFamiliarFlight(pos.row, pos.col, body, face)
    }

    /** Standard jellies on the board that are not already [targetPieceId]. */
    private fun replaceableJellies(targetPieceId: String): List<Int> =
        world.getEntitiesForAspect(
            Aspect.all(
                JellyTypeComponent::class,
                DraggableComponent::class,
                GridPositionComponent::class,
            ),
        ).filter { id ->
            world.isFamily(id, pieceSet, Family.JELLY) && world.pieceIdOf(id) != targetPieceId
        }

    private fun firstIncompleteGoalPieceId(): String? =
        world.boardEntityId()?.let { goalsMapper[it] }?.goals?.firstOrNull { !it.completed }?.pieceId

    private fun selectTargetPieceId(goals: List<Goal>): String? {
        if (goals.isEmpty()) return null
        val maxRemaining = goals.maxOf { it.remainingQuantity }
        val candidates = goals.filter { it.remainingQuantity == maxRemaining }
        if (candidates.size == 1) return candidates[0].pieceId
        val boardCounts = countJelliesByPieceId()
        return candidates.minByOrNull { boardCounts[it.pieceId] ?: 0 }?.pieceId
    }

    private fun countJelliesByPieceId(): Map<String, Int> {
        val counts = mutableMapOf<String, Int>()
        for (id in world.getEntitiesForAspect(
            Aspect.all(JellyTypeComponent::class, DraggableComponent::class),
        )) {
            if (!world.isFamily(id, pieceSet, Family.JELLY)) continue
            val pieceId = world.pieceIdOf(id) ?: continue
            counts[pieceId] = (counts[pieceId] ?: 0) + 1
        }
        return counts
    }

    private companion object {
        const val CONJURER_REPLACE_COUNT = 3
    }
}
